import { useEffect, useState, useCallback, useMemo } from 'react'
import { Link, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@/hooks/useWallet.ts'
import { useVaults } from '@/hooks/useVaults.ts'
import { useBeneficiary } from '@/hooks/useBeneficiary.ts'
import { useSubscription } from '@/hooks/useSubscription.ts'
import { useProfile } from '@/hooks/useProfile.ts'
import { useVaultCountdownEmailAlerts } from '@/hooks/useVaultCountdownEmailAlerts.ts'
import { useSubscriptionLifecycleEmails } from '@/hooks/useSubscriptionLifecycleEmails.ts'
import type { Vault } from '@/services/contract/contract.types.ts'
import { VaultCard } from './VaultCard.tsx'

export function DashboardPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const { shortenedAddress, disconnect, address } = useWallet()
  const {
    vaultIds,
    fetchMyVaults,
    fetchVaultDetails,
    getTimeRemaining,
    loading: vaultsLoading,
  } = useVaults()
  const {
    assignedVaultIds,
    fetchAssignedVaults,
    loading: beneficiaryLoading,
  } = useBeneficiary()
  const { isActive, checkActive, fetchSubscription, subscription } =
    useSubscription()
  const { profile, fetchProfile } = useProfile()

  const [myVaults, setMyVaults] = useState<Vault[]>([])
  const [assignedVaults, setAssignedVaults] = useState<Vault[]>([])

  const loadData = useCallback(async () => {
    await Promise.all([
      fetchMyVaults(),
      fetchAssignedVaults(),
      checkActive(),
      fetchSubscription(),
      fetchProfile(),
    ])
  }, [
    fetchMyVaults,
    fetchAssignedVaults,
    checkActive,
    fetchSubscription,
    fetchProfile,
  ])

  const allVaults = useMemo(
    () => [...myVaults, ...assignedVaults],
    [myVaults, assignedVaults],
  )

  const getTimeRemainingSafe = useCallback(
    async (vaultId: bigint) => {
      const r = await getTimeRemaining(vaultId)
      return r ?? 0n
    },
    [getTimeRemaining],
  )

  const onDashboard = location.pathname === '/dashboard' && Boolean(address)

  useVaultCountdownEmailAlerts(allVaults, getTimeRemainingSafe, onDashboard)

  useSubscriptionLifecycleEmails(
    subscription,
    isActive,
    profile,
    address,
    onDashboard,
  )

  // Refetch when navigating to dashboard so new vaults appear (e.g. after create)
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      loadData()
    }
  }, [location.pathname, loadData])

  useEffect(() => {
    const loadDetails = async () => {
      const vaults: Vault[] = []
      for (const id of vaultIds) {
        // Vault details can be temporarily unavailable right after a new
        // contract write (provider propagation / RPC flakiness). Retry so
        // we don't end up displaying only a subset.
        let v: Vault | null = null
        for (let attempt = 0; attempt < 3; attempt++) {
          v = await fetchVaultDetails(id)
          if (v) break
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)))
        }
        if (v) vaults.push(v)
      }
      setMyVaults(vaults)
    }
    if (vaultIds.length > 0) loadDetails()
    else {
      const id = setTimeout(() => setMyVaults([]), 0)
      return () => clearTimeout(id)
    }
  }, [vaultIds, fetchVaultDetails])

  useEffect(() => {
    const loadDetails = async () => {
      const vaults: Vault[] = []
      for (const id of assignedVaultIds) {
        let v: Vault | null = null
        for (let attempt = 0; attempt < 3; attempt++) {
          v = await fetchVaultDetails(id)
          if (v) break
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)))
        }
        if (v) vaults.push(v)
      }
      setAssignedVaults(vaults)
    }
    if (assignedVaultIds.length > 0) loadDetails()
    else {
      const id = setTimeout(() => setAssignedVaults([]), 0)
      return () => clearTimeout(id)
    }
  }, [assignedVaultIds, fetchVaultDetails])

  const isLoading = vaultsLoading || beneficiaryLoading

  return (
    <div className="max-w-content mx-auto px-4 py-8 md:px-8 md:py-12">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4 max-md:flex-col">
        <div>
          <h1 className="text-fg text-[1.75rem] font-bold max-md:text-[1.375rem]">
            {t('dashboard.title')}
          </h1>
          <p className="text-fg-secondary mt-1">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <span className="border-accent/20 bg-accent-muted text-accent max-w-[180px] truncate rounded-md border px-3 py-1.5 font-mono text-[0.8125rem]">
            {shortenedAddress}
          </span>
          <button
            type="button"
            className="border-line text-fg-secondary duration-fast hover:border-error hover:text-error cursor-pointer rounded-md border bg-transparent px-3 py-1.5 text-[0.8125rem] font-medium transition-all"
            onClick={disconnect}
          >
            {t('dashboard.disconnect')}
          </button>
        </div>
      </div>

      <div className="mb-12">
        {isActive ? (
          <Link
            to="/dashboard/create-vault"
            className="bg-gradient-brand text-fg-inverse duration-fast inline-block rounded-md px-6 py-3 text-[0.9375rem] font-semibold no-underline transition-opacity hover:opacity-90"
          >
            {t('dashboard.createVault')}
          </Link>
        ) : (
          <div className="flex flex-wrap items-center gap-4">
            <span className="border-line bg-surface-muted text-fg-muted inline-block cursor-not-allowed rounded-md border px-6 py-3 text-[0.9375rem] font-semibold opacity-60">
              {t('dashboard.createVault')}
            </span>
            <span className="text-fg-muted text-[0.8125rem]">
              <Link to="/benefactor" className="text-accent underline">
                {t('dashboard.subscribeTo')}
              </Link>{' '}
              {t('dashboard.toCreateVaults')}
            </span>
          </div>
        )}
      </div>

      <section className="mb-12">
        <h2 className="text-fg mb-6 text-xl font-semibold">
          {t('dashboard.myVaults')}
        </h2>
        {isLoading ? (
          <div className="border-line flex min-h-[160px] items-center justify-center rounded-xl border border-dashed max-md:min-h-[120px]">
            <p className="text-fg-muted p-4 text-center text-[0.9375rem]">
              {t('dashboard.loadingVaults')}
            </p>
          </div>
        ) : myVaults.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-6">
            {myVaults.map((vault) => (
              <VaultCard
                key={vault.id.toString()}
                vault={vault}
                role="benefactor"
                onVaultUpdated={loadData}
              />
            ))}
          </div>
        ) : (
          <div className="border-line flex min-h-[160px] items-center justify-center rounded-xl border border-dashed max-md:min-h-[120px]">
            <p className="text-fg-muted p-4 text-center text-[0.9375rem]">
              {t('dashboard.noVaults')}
            </p>
          </div>
        )}
      </section>

      <section className="mb-12">
        <h2 className="text-fg mb-6 text-xl font-semibold">
          {t('dashboard.assignedToMe')}
        </h2>
        {isLoading ? (
          <div className="border-line flex min-h-[160px] items-center justify-center rounded-xl border border-dashed max-md:min-h-[120px]">
            <p className="text-fg-muted p-4 text-center text-[0.9375rem]">
              {t('dashboard.loadingAssigned')}
            </p>
          </div>
        ) : assignedVaults.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] gap-6">
            {assignedVaults.map((vault) => (
              <VaultCard
                key={vault.id.toString()}
                vault={vault}
                role="beneficiary"
                onVaultUpdated={loadData}
              />
            ))}
          </div>
        ) : (
          <div className="border-line flex min-h-[160px] items-center justify-center rounded-xl border border-dashed max-md:min-h-[120px]">
            <p className="text-fg-muted p-4 text-center text-[0.9375rem]">
              {t('dashboard.noAssigned')}
            </p>
          </div>
        )}
      </section>
    </div>
  )
}
